import React, { useEffect, useState } from 'react';
import { Image, message } from 'antd';
import { useCookies } from 'react-cookie';
import { arrayMoveImmutable } from 'array-move';
import { MenuOutlined } from '@ant-design/icons';
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc';
import { PublicState as PublicStateList } from 'utils/constant';
import { promotionApis } from 'apis/operation/promotionApi';
import { cashingApis } from 'apis/common/cashingApis';
import DefaultLayout from 'components/layout/DefaultLayout';
import TableToolbar from 'components/table/TableToolbar';
import SearchInput from 'components/search/SearchInput';
import MainTable from 'components/table/MainTable';
import NoImage from 'components/etc/NoImage';
import DeletePopConfirm from 'components/button/DeletePopConfirm';
import PublicState from 'components/etc/PublicState';
import PromotionInputModal from './PromotionInputModal';
import DeployModal from 'components/form/DeployModal';
import TextButton from 'components/button/TextButton';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useNavigate } from 'react-router-dom';
import CapsuleButton from 'components/button/CapsuleButton';
import { useTranslation } from 'react-i18next';
/**
 * * 운영 관리 - 프로모션 목록
 */

// 정렬 아이콘
const DragHandle = sortableHandle(() => (
  <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
));
const SortableItem = sortableElement((props) => <tr {...props} />);
const SortableContainer = sortableContainer((props) => <tbody {...props} />);

// ! START
const PromotionListPage = () => {
  const { t } = useTranslation();
  const publicState = PublicStateList();
  const [cookies] = useCookies();
  const authUpdate = JSON.parse(cookies?.managementAuth);
  const navigate = useNavigate();

  const [searchData, setSearchData] = useState();
  const [visible, setVisible] = useState(false);
  const [deployModalOpen, setDeployModalOpen] = useState(false); // 배포 모달 visible
  const [promotionDataList, setPromotionDataList] = useState();
  const [selectData, setSelectData] = useState();
  // console.log(promotionDataList);

  const handleOpen = (record) => (e) => {
    if (e.target.name === 'deploy') {
      setDeployModalOpen(true);
    } else {
      setSelectData(record);
      setVisible(true);
    }
  };

  /// 등록 & 수정 페이지 이동
  const handleLink = (record) => () => {
    navigate(`${record.promotion_no}`, {
      state: { record: record },
    });
  };

  /// 정렬 드래깅이 끝날 때 => api 호출
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        [].concat(promotionDataList),
        oldIndex,
        newIndex,
      ).filter((el) => !!el);

      let sortData = newData.map((v, i) => {
        v.sortkey = i;
        return v;
      });

      const res = await promotionApis.sorting(sortData);

      if (!res) {
        return false;
      }
      // 캐싱 비우기
      await cashingApis.notice_promotion_cash();
      setPromotionDataList(sortData);
      message.success('프로모션이 재정렬 되었습니다.');
    }
  };
  const DraggableContainer = (props) => {
    return (
      <SortableContainer
        useDragHandle
        disableAutoscroll
        helperClass='row-dragging' // 정렬 아이콘 클릭 된 상태로 움직일 때의 모습 스타일링
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  };

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index

    // ***** int로 변환해주었어야 했다!
    const index = promotionDataList.findIndex(
      (item) => parseInt(item.key) === parseInt(restProps['data-row-key']),
    );
    return <SortableItem index={index} {...restProps} />;
  };

  /// 삭제
  const handleDelete = (record) => async () => {
    const res = await promotionApis.delete(record.promotion_no);
    if (!res) {
      return false;
    }
    // 캐싱 비우기
    await cashingApis.notice_promotion_cash();
    fetchData();
    message.success('프로모션이 삭제되었습니다.');
  };

  const fetchData = async () => {
    const res = await promotionApis.read();
    let newArr = [];
    /// 정렬하기 위해서 key 값 필요
    res?.map((v, i) => newArr.push({ ...v, key: i }));
    setPromotionDataList(newArr);
  };

  const ColumnsOrigin = [
    {
      title: 'NO',
      dataIndex: 'promotion_no',
      width: '3rem',
      align: 'center',
    },
    {
      title: t('promotion.table.image'),
      dataIndex: 'image_url',
      width: '10rem',
      align: 'center',
      render: (t, record) => (
        <>
          {record.image_url === null || record.image_url === '' ? (
            <NoImage />
          ) : (
            <Image src={record.image_url} height={60} />
          )}
        </>
      ),
    },
    {
      title: t('promotion.table.imageName'),
      dataIndex: 'title',
      width: '21rem',
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text, record) => (
        // <TextButton onClick={handleLink(record)}>{text}</TextButton>
        <TextButton onClick={handleOpen(record)} disabled={authUpdate}>
          {text}
        </TextButton>
      ),
    },
    {
      title: t('promotion.table.path'),
      dataIndex: 'location_name',
      width: '21rem',
      sorter: (a, b) => a.location_name.localeCompare(b.location_name),
    },
    {
      title: t('promotion.table.publicStatus'),
      dataIndex: 'is_public',
      width: '10rem',
      filters: publicState,
      onFilter: (value, record) => record?.is_public === value,
      sorter: (a, b) => a.is_public - b.is_public,
      render: (text) => <PublicState value={text} />,
    },
    {
      title: t('promotion.table.startDate'),
      dataIndex: 'start_date',
      width: '12rem',
      sorter: (a, b) => a.start_date.localeCompare(b.start_date),
    },
    {
      title: t('promotion.table.endDate'),
      dataIndex: 'end_date',
      width: '13rem',
      sorter: (a, b) => a.end_date.localeCompare(b.end_date),
    },
    {
      title: t('promotion.table.action'),
      width: '6rem',
      align: 'center',
      render: (text, record) => {
        return (
          <div>
            {/* <CapsuleButton onClick={handleOpen(record)} disabled={authUpdate}>
              수정
            </CapsuleButton> */}
            <DeletePopConfirm
              title={t('promotion.table.deleteMessage')}
              onConfirm={handleDelete(record)}
              disabled={authUpdate}
            />
          </div>
        );
      },
    },
    {
      title: t('promotion.table.sort'),
      width: '5rem',
      render: () => <DragHandle disabled={authUpdate} />,
    },
  ];

  useEffect(() => {
    fetchData().catch(console.log);
  }, []);

  return (
    <>
      <DefaultLayout>
        <TableToolbar>
          <SearchInput
            placeholder={t('promotion.header.inputPlaceHolder')}
            setSearchData={setSearchData}
          />
          <div>
            <DefaultButton
              name='register'
              onClick={handleOpen()}
              disabled={authUpdate}
            >
              {t('promotion.header.registerPromotion')}
            </DefaultButton>
            <DefaultButton
              name='deploy'
              onClick={handleOpen()}
              disabled={authUpdate}
              backColor={`${theme.colors.server_orange}`}
            >
              {t('promotion.header.LIVEDeployment')}
            </DefaultButton>
          </div>
        </TableToolbar>
        <MainTable
          columns={ColumnsOrigin}
          dataSource={
            searchData
              ? promotionDataList?.filter(
                  (data) =>
                    data.title.indexOf(searchData) !== -1 ||
                    data.location_name.indexOf(searchData) !== -1,
                )
              : promotionDataList
          }
          rowKey={(record) => `${record.key}`}
          components={
            promotionDataList && {
              body: {
                style: { color: 'blue' },
                wrapper: DraggableContainer,
                row: DraggableBodyRow,
              },
            }
          }
        />
      </DefaultLayout>

      {/* 모달 */}
      {visible && (
        <PromotionInputModal
          visible={visible}
          setVisible={setVisible}
          record={selectData}
          fetchData={fetchData}
        />
      )}
      {deployModalOpen && (
        <DeployModal
          title={t('promotion.promotionInputModal.title')}
          kind='promotion'
          visible={deployModalOpen}
          setVisible={setDeployModalOpen}
          fetchData={fetchData}
        />
      )}
    </>
  );
};

export default PromotionListPage;
